import { ariaTristate, ariaTrueFalse } from '@disy/lib/aria';
import { ChangeEvent } from '@disy/lib/event-util';
import type { ObservedAttribute } from '@disy/lib/html-element-util';
import { FormElement } from '@disy/ui/form-element';

import './switch.css';
import styles from './switch.module.css' with { type: 'css' };

export interface SwitchOptions {
  name?: string;
  checked?: boolean;
  disabled?: boolean;
}

const COMPONENT_NAME = 'd-switch';

/**
 * @attr name
 */
export class Switch extends FormElement<boolean> {
  static override get observedAttributes() {
    return [...super.observedAttributes, 'aria-checked'] as const;
  }

  /**
   * @param [options] - Options
   * @param [options.checked] - Whether the switch is initially checked
   * @param [options.disabled] - Whether the switch is initially disabled
   */
  constructor({ name, checked, disabled }: SwitchOptions = {}) {
    super({
      name,
      defaultValue: false,
      disabled,
    });

    const shadowRoot = this.attachShadow({ mode: 'open' });
    shadowRoot.adoptedStyleSheets = [styles];
    const handle = document.createElement('div');
    handle.classList.add('handle');
    shadowRoot.append(handle);

    this.defaultValue = checked ?? this.getAttribute('aria-checked') === 'true';

    this.addEventListener('click', (event) => {
      if (!this.disabled) {
        event.preventDefault(); // Prevent another click on the <label>.
        this.checked = !this.checked;
        this.dispatchEvent(new SwitchChangeEvent(this.checked));
      }
    });
  }

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(COMPONENT_NAME, 'keyboard-click');
    this.setAttribute('role', 'switch');
    this.tabIndex = 0;
  }

  override attributeChangedCallback(name: ObservedAttribute<typeof Switch>) {
    if (name === 'aria-checked') {
      const attributeValue = ariaTrueFalse(this, 'checked');
      if (attributeValue !== this.value) {
        this.value = attributeValue;
      }
      return;
    }

    super.attributeChangedCallback(name);
  }

  protected override applyValue(value: boolean) {
    ariaTristate(this, 'checked', value);
  }

  set checked(value) {
    this.value = value;
  }

  get checked() {
    return this.value;
  }

  set defaultChecked(value) {
    this.defaultValue = value;
  }

  get defaultChecked() {
    return this.defaultValue;
  }
}

customElements.define(COMPONENT_NAME, Switch);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: Switch;
  }
}

export class SwitchChangeEvent extends ChangeEvent<{ checked: boolean }> {
  constructor(checked: boolean) {
    super({ detail: { checked } });
  }
}
